import authService from '../services/auth/authService';
import tokenManager from '../services/auth/tokenManager';
import { getCurrentOrgSlug, getTenantScopedStorageKey, buildStateAwarePath, stripStatePrefix } from './tenant';
import { resetAuthBootstrapState } from '../state/authBootstrapState';
import { clearBulkUploadJob } from './bulkUploadTracker';

const determineDefaultRedirect = (user) => {
  const currentPath = stripStatePrefix(window.location.pathname || '');
  const role = String(user?.originalRole || user?.role || '').toLowerCase();

  if (currentPath.startsWith('/platform') || role.includes('platform')) {
    return '/platform/login';
  }

  if (role.includes('consumer')) {
    return buildStateAwarePath('/login/consumer');
  }

  return buildStateAwarePath('/login/b2b');
};

/**
 * Local cleanup — always runs regardless of whether the backend
 * revoke succeeded. Clears localStorage, sessionStorage, and
 * resets bootstrap state so the next load starts fresh.
 */
function localCleanup(orgSlug) {
  if (orgSlug) {
    const redirectKey = getTenantScopedStorageKey('redirectAfterLogin', orgSlug);
    localStorage.removeItem(redirectKey);
    Object.keys(localStorage)
      .filter((key) => key.startsWith('mira_auth:applyContext:') && key.includes(orgSlug))
      .forEach((key) => localStorage.removeItem(key));
  }
  sessionStorage.removeItem('redirectAfterLogin');

  resetAuthBootstrapState();
  clearBulkUploadJob();
  tokenManager.clearToken();

  const tenantSlug = (window.location.hostname.split('.')[0] || 'default').toLowerCase();
  localStorage.removeItem(`mira_session_metadata_${tenantSlug}`);
  localStorage.removeItem(`mira_session_refresh_leader_${tenantSlug}`);
  localStorage.removeItem('mira_session_metadata');
  localStorage.removeItem('mira_session_refresh_leader');
}

export async function performLogout({ redirect = true, redirectTo } = {}) {
  const sessionToken = tokenManager.getSessionToken();
  const cachedUser = tokenManager.getUserData();
  const orgSlug = getCurrentOrgSlug();

  try {
    await authService.logout(sessionToken || null, { skipRedirect: true });
  } catch (error) {
    if (!import.meta.env.PROD) {
      console.error('[Logout] Failed to revoke session, continuing local cleanup', error);
    }
  } finally {
    // Always clean up local state — even if the revoke API failed,
    // timed out, or threw. Without this, a failed revoke leaves
    // stale localStorage that causes dead-page redirects on next load.
    localCleanup(orgSlug);
  }

  if (redirect) {
    const target = redirectTo || determineDefaultRedirect(cachedUser);
    window.location.replace(target);
  }

  return { success: true };
}
